import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from './kit';
import { toast } from './toasts';

// Workspaces, as Stripe accounts: one per website you sell for, switched from the top of the menu. Test mode is
// the same workspace's sandbox (fake data, no credits, nothing sent).

type MyOrgs = { data: Array<{ id: string; name: string; primary_domain: string; is_agency?: boolean; agency_name?: string | null }>; active_org_id: string | null };

/** Switch the session to another org (or its sandbox), then reload what the app knows. */
export function useSwitch() {
  const { supabase, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const run = async (call: () => Promise<unknown>) => {
    setBusy(true);
    try { await call(); await supabase?.auth.refreshSession(); await refresh(); nav('/'); }
    catch (e) { toast(`Could not switch: ${(e as Error).message}`, undefined, undefined, true); }
    finally { setBusy(false); }
  };
  return { busy, run };
}

/** `inline`: the list itself, always shown (the rail's workspace pop-up), instead of a button that opens it. */
export function WorkspaceSwitcher({ inline = false, onDone }: { inline?: boolean; onDone?: () => void } = {}) {
  const { me } = useAuth();
  const orgs = useApi<MyOrgs>(me ? '/portal/my-orgs' : null);
  const [openState, setOpen] = useState(false);
  const open = inline || openState;
  const box = useRef<HTMLDivElement>(null);
  const { busy, run } = useSwitch();
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  if (!me) return null;
  const activeId = me.org.sandbox_of ?? me.org.id;
  const current = orgs.data?.data.find((o) => o.id === activeId);
  const name = current?.name ?? me.org.name.replace(/ \(sandbox\)$/, '');
  return (
    <div className={`ws${inline ? ' inline' : ''}`} ref={box}>
      {inline ? null : <button type="button" className="ws-current" aria-haspopup="menu" aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen(!open); }} disabled={busy}>
        <span className="ws-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
        <span className="ws-text"><b>{name}</b><small>{current?.primary_domain ?? me.org.primary_domain}</small></span>
        <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 9l4-4 4 4M8 15l4 4 4-4" /></svg>
      </button>}
      {open ? (
        <div className="ws-menu" role="menu" onClick={(e) => e.stopPropagation()}>
          <div className="ws-label">Workspaces</div>
          {(orgs.data?.data ?? []).map((o) => (
            <button key={o.id} type="button" role="menuitemradio" aria-checked={o.id === activeId} className="ws-item" disabled={busy}
              onClick={() => { setOpen(false); onDone?.(); if (o.id !== activeId) void run(() => api('/portal/switch-org', { method: 'POST', body: { org_id: o.id } })); }}>
              <span className="ws-avatar small" aria-hidden="true">{o.name.slice(0, 1).toUpperCase()}</span>
              <span className="ws-text"><b>{o.name}</b><small>{o.primary_domain}{o.agency_name ? ` · client of ${o.agency_name}` : o.is_agency ? ' · agency' : ''}</small></span>
              {o.id === activeId ? <svg className="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : null}
            </button>
          ))}
          <Link role="menuitem" to="/workspace/new" className="ws-new" onClick={() => { setOpen(false); onDone?.(); }}>
            <span className="ws-avatar small plus" aria-hidden="true">+</span><span>New workspace <small>for another website</small></span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/** Test mode on and off, as a switch. */
export function TestModeToggle() {
  const { me } = useAuth();
  const { busy, run } = useSwitch();
  if (!me) return null;
  const on = Boolean(me.org.sandbox_of);
  return (
    // not a <label>: the switch is named once, by its aria-label
    <div className={`testmode${on ? ' on' : ''}`}>
      <span aria-hidden="true">Test mode</span>
      <button type="button" role="switch" aria-checked={on} aria-label="Test mode" disabled={busy}
        onClick={() => void run(() => api('/portal/test-mode', { method: 'POST', body: { on: !on } }))}><span /></button>
    </div>
  );
}

/** The bar across the top while in test mode. */
export function TestModeBar() {
  const { me } = useAuth();
  const { busy, run } = useSwitch();
  if (!me?.org.sandbox_of) return null;
  return (
    <div className="testmode-bar" role="status">
      <b>Test mode</b> · Sandbox data: fake companies and contacts. Nothing is sent and no credits are used.
      <button type="button" className="link" disabled={busy} onClick={() => void run(() => api('/portal/test-mode', { method: 'POST', body: { on: false } }))}>Leave test mode</button>
    </div>
  );
}
